import * as React from 'react';
import {
  PhoneInput as ReactPhoneInput,
  defaultCountries,
  type PhoneInputRefType,
} from 'react-international-phone';
import 'react-international-phone/style.css';
import { cn } from '@/lib/utils';
import { normalizePhoneToE164 } from '@/lib/phone';

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> {
  value?: string;
  onChange?: (phone: string) => void;
  defaultCountry?: string;
  className?: string;
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    { value, onChange, defaultCountry = 'us', placeholder, disabled, className, ...inputProps },
    ref,
  ) => {
    const isReadOnly = Boolean(inputProps.readOnly);
    const handleChange = React.useCallback(
      (phone: string, meta: { country: { dialCode: string } }) => {
        const dialCodeOnly = phone === `+${meta.country.dialCode}`;
        if (dialCodeOnly) {
          onChange?.('');
          return;
        }
        const normalized = normalizePhoneToE164(phone);
        if (!value && normalized === '') return;
        onChange?.(normalized);
      },
      [onChange, value],
    );

    return (
      <div
        className={cn('phone-input-wrapper', className)}
        data-readonly={isReadOnly ? 'true' : undefined}
      >
        <ReactPhoneInput
          ref={ref as React.Ref<PhoneInputRefType>}
          defaultCountry={defaultCountry}
          value={value}
          onChange={isReadOnly ? undefined : handleChange}
          forceDialCode
          placeholder={placeholder}
          disabled={disabled}
          hideDropdown={isReadOnly}
          name={inputProps.name}
          onBlur={inputProps.onBlur}
          onFocus={inputProps.onFocus}
          required={inputProps.required}
          autoFocus={inputProps.autoFocus}
          inputProps={inputProps}
          inputClassName={cn(
            'flex h-10 w-full rounded-md rounded-l-none border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            '!border-l-0',
          )}
          countrySelectorStyleProps={{
            buttonClassName: cn(
              'flex h-10 items-center gap-1 rounded-l-md border border-input bg-background px-3 py-2 text-sm ring-offset-background hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              '!rounded-r-none',
            ),
            dropdownStyleProps: {
              className: cn(
                'z-50 min-w-[200px] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95',
              ),
              listItemClassName: cn(
                'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
              ),
            },
          }}
          countries={defaultCountries}
        />
        <style>{`
          .phone-input-wrapper .react-international-phone-input-container {
            display: flex;
            width: 100%;
          }
          .phone-input-wrapper .react-international-phone-country-selector-button,
          .phone-input-wrapper .react-international-phone-input {
            height: 2.5rem;
            background-color: var(--background);
            border-color: var(--input);
            color: var(--foreground);
          }
          .phone-input-wrapper .react-international-phone-input {
            flex: 1;
          }
          .phone-input-wrapper .react-international-phone-input::placeholder {
            color: var(--muted-foreground);
          }
          .phone-input-wrapper .react-international-phone-country-selector-button {
            border-right: none;
          }
          .phone-input-wrapper[data-readonly="true"] .react-international-phone-country-selector-button:disabled {
            cursor: default;
            opacity: 1;
          }
          .phone-input-wrapper[data-readonly="true"] .react-international-phone-input {
            cursor: default;
          }
          .phone-input-wrapper[data-readonly="true"] .react-international-phone-flag-emoji {
            opacity: 1;
          }
          .phone-input-wrapper .react-international-phone-country-selector-dropdown {
            max-height: 200px;
            overflow-y: auto;
          }
          .dark .phone-input-wrapper .react-international-phone-country-selector-dropdown {
            background-color: var(--popover);
            border-color: var(--border);
          }
          .dark .phone-input-wrapper .react-international-phone-country-selector-dropdown__list-item:hover {
            background-color: var(--accent);
          }
        `}</style>
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
